/**
 * The host dashboard rail — README "Host rail". GREENLIGHT (the loud primary action on a
 * Party Starter that is still funding, with the EVENT FUND bar), DOOR MODE (black → the
 * DOOR tab), HOST ACTIONS (the pill list with a trailing →: PUBLISH / POST AN UPDATE / ASK
 * FOR MORE / SHARE / BROADCAST / VIEW PUBLIC PAGE / DUPLICATE / DELETE / CANCEL) over the
 * ADDRESS note, and PAYOUT (the MONEY ledger in small, YOURS, the release time).
 * Sticky on desktop, stacked under the tab on mobile — the page owns that placement.
 */
import React from 'react';
import { Card, Btn, Note, KV, Yours, Track, Tag } from './kit';
import { money, whenLabel } from '../../utils/surfaceFormat';
import { BROADCAST_STATUSES, CANCELABLE_STATUSES, DOOR_STATUSES, escrowReleaseMoment } from './hostOpsModel';

/** One HOST ACTIONS pill: label on the left, → on the right. `fill` = black, `danger` = red. */
function Action({ children, fill = false, danger = false, testId, ...rest }) {
  return (
    <button type="button" className="hev-action" data-fill={fill ? '' : undefined} data-danger={danger ? '' : undefined} data-testid={testId} {...rest}>
      <span>{children}</span>
      <span aria-hidden="true">→</span>
    </button>
  );
}

export default function HostRail({ event, mobile = false, funding, moneyDto, busy = {}, onGoTab, onGreenlight, onShare, onBroadcast, onDuplicate, onPublish, onDelete, onCancel, onViewPublic }) {
  const status = event?.status;
  const isFree = (event?.ticketPriceCents || 0) === 0;
  const starter = event?.fundingType === 'party_starter';
  const canGreenlight = starter && status === 'funding';
  const pct = Math.round(funding?.percentage || 0);
  const release = escrowReleaseMoment(event, moneyDto);

  return (
    <>
      {canGreenlight && (
        <Card pad="rail" testId="greenlight-card" style={{ gap: 14 }}>
          <div className="hev-rail-head">
            <span className="hev-rail-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="hev-pulse" aria-hidden="true" />GREENLIGHT
            </span>
            {!isFree && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#FF00AA', whiteSpace: 'nowrap' }}>{pct}% FUNDED</span>}
          </div>
          {!isFree && (
            <span className="hev-fund">
              <span className="hev-fund-head">
                <span className="hev-fund-k">EVENT FUND</span>
                <span className="hev-fund-v">{money(funding?.currentCents || 0, { whole: true })}<span> / {money(funding?.targetCents || 0, { whole: true })}</span></span>
              </span>
              <Track pct={pct} />
            </span>
          )}
          <span style={{ fontSize: 12, lineHeight: 1.6 }}>
            {isFree
              ? 'Greenlighting locks the guest list and reveals the address to everyone who is in.'
              : pct >= 100
                ? 'The goal is met. Greenlighting charges every accepted guest and releases the address.'
                : `Funding goal is ${pct}% met — you can still greenlight early. Every accepted guest is charged.`}
          </span>
          <Btn variant="ring-black" size="cta" full onClick={onGreenlight} disabled={!!busy.greenlight} testId="greenlight-button">
            {busy.greenlight ? 'GREENLIGHTING…' : isFree || pct >= 100 ? 'GREENLIGHT EVENT' : `GREENLIGHT — ${pct}% FUNDED`}
          </Btn>
        </Card>
      )}

      {DOOR_STATUSES.includes(status) && (
        <div className="hev-door" data-testid="door-mode-card">
          <span className="hev-door-t">DOOR MODE</span>
          <span className="hev-door-s">Scan tickets at the door. Works offline; syncs when you're back on signal.</span>
          <Btn variant="white" size="md" full onClick={() => onGoTab('door')}>OPEN SCANNER</Btn>
        </div>
      )}

      <Card pad="rail" gap="8" testId="host-actions-card">
        <span className="hev-rail-title" style={{ paddingBottom: 4 }}>HOST ACTIONS</span>
        {status === 'draft' && <Action fill onClick={onPublish} disabled={!!busy.publish}>{busy.publish ? 'PUBLISHING…' : 'PUBLISH EVENT'}</Action>}
        <Action onClick={() => onGoTab('updates')}>POST AN UPDATE</Action>
        <Action onClick={() => onGoTab('mime')}>ASK FOR MORE</Action>
        <Action onClick={onShare}>SHARE EVENT</Action>
        {BROADCAST_STATUSES.includes(status) && <Action onClick={onBroadcast} title="Send a message to every accepted/paid attendee">BROADCAST</Action>}
        <Action onClick={onViewPublic}>VIEW PUBLIC PAGE</Action>
        <Action onClick={onDuplicate} disabled={!!busy.duplicate}>{busy.duplicate ? 'DUPLICATING…' : 'DUPLICATE EVENT'}</Action>
        {status === 'draft' && <Action danger onClick={onDelete} disabled={!!busy.delete}>{busy.delete ? 'DELETING…' : 'DELETE EVENT'}</Action>}
        {CANCELABLE_STATUSES.includes(status) && (
          <Action danger onClick={onCancel} disabled={!!busy.cancel} testId="cancel-event">
            {busy.cancel ? 'CANCELING…' : isFree ? 'CANCEL EVENT' : 'CANCEL + REFUND ALL'}
          </Action>
        )}
        <div style={{ marginTop: 8 }}>
          <Note tone="grey" small tag="ADDRESS">
            {event?.isExternalTicketing
              ? 'Tickets are sold on another site — the address is whatever the seller publishes.'
              : starter
                ? 'Released automatically — guests see the exact address the moment you greenlight. Nothing for you to do.'
                : 'Released automatically — guests see the exact address the moment they pay. Nothing for you to do.'}
          </Note>
        </div>
      </Card>

      {!event?.isExternalTicketing && !isFree && (
        <Card pad="rail" gap="10" testId="payout-card">
          <div className="hev-rail-head">
            <span className="hev-rail-title">PAYOUT</span>
            {moneyDto?.estimated && <Tag small>ESTIMATED</Tag>}
          </div>
          <KV small k="Ticket revenue" v={money(moneyDto?.ticketRevenueCents)} />
          {moneyDto?.refundedCents > 0 && <KV small k="Refunded" v={money(-moneyDto.refundedCents)} />}
          {starter && <KV small k="Party Starter fee (7%)" v={money(-(moneyDto?.hostFeeCents || 0))} />}
          {starter && <KV small k="GST on the fee (5%)" v={money(-(moneyDto?.hostFeeGstCents || 0))} />}
          <Yours small value={money(moneyDto?.netToHostCents)} />
          <span style={{ fontSize: 11, lineHeight: 1.55, color: '#6B7280' }}>
            {release && release <= new Date()
              ? 'Released to your wallet.'
              : release
                ? `Released ${whenLabel(release)} — 24h after doors. Held in escrow until then.`
                : 'Released 24h after doors. Held in escrow until then.'}
          </span>
          <Btn variant="plain" self="start" onClick={() => onGoTab('money')} style={{ height: 'auto', padding: 0, fontSize: 10 }}>EVERY TRANSACTION →</Btn>
        </Card>
      )}
    </>
  );
}
